import { z } from 'zod';

/**
 * Validated app config. BUILD_STANDARDS §13: config comes from
 * VITE_-prefixed env vars, read once, validated at startup. A missing
 * or malformed var throws IMMEDIATELY — the app should never boot into
 * a half-configured state.
 */
const configSchema = z.object({
  apiBaseUrl: z.string().url().default('http://localhost:4000'),
  enableDevtools: z.boolean().default(false),
});

export type AppConfig = z.infer<typeof configSchema>;

function parseConfig(): AppConfig {
  // Treat empty string as "not set" so the Zod default kicks in — common
  // when a build inherits `VITE_API_BASE_URL=` from an unfilled .env.
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || undefined;
  const raw = {
    apiBaseUrl,
    enableDevtools: import.meta.env.VITE_ENABLE_DEVTOOLS === 'true',
  };
  const parsed = configSchema.safeParse(raw);
  if (!parsed.success) {
    // Fail fast + loud so a misconfigured deploy shows up as a startup
    // crash, not a mysterious runtime bug.
    const details = parsed.error.issues
      .map((i) => `  ${i.path.join('.') || '<root>'}: ${i.message}`)
      .join('\n');
    throw new Error(`Invalid frontend configuration:\n${details}`);
  }
  return parsed.data;
}

export const config = parseConfig();
